import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Filter, Eye, AlertTriangle, CheckCircle, Clock, User, FileText, ChevronDown, ChevronUp, BarChart3, Target, HelpCircle } from 'lucide-react';
import { getClientDoelenByName, findDoelById } from '../../data/clientDoelen';
import { CLIENTEN } from './RapporteerHelperPage';
import { oudeZonderNieuweTweeling } from '../../utils/rapportageSleutel';

const RapportageControlePage = ({ onBack, user }) => {
  // BEIDE bronnen gebruiken voor backwards compatibility
  const [sjablonen, setSjablonen] = useState([]); // Oude rapportage-sjablonen
  const [sjabloonRapportages, setSjabloonRapportages] = useState([]); // Nieuwe sjabloon-flow rapportages
  const [alleRapportages, setAlleRapportages] = useState([]); // Gecombineerde lijst
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRapportage, setSelectedRapportage] = useState(null); // Detail view
  const [filters, setFilters] = useState({
    begeleider: '',
    client: '',
    datumVan: '',
    datumTot: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'zorgmanager';

  // Correctie van verkeerd opgeslagen rapportages (cliënt / doelen)
  const [correctie, setCorrectie] = useState({ client: '', doelenWissen: false, bezig: false, melding: '' });
  const alleClientNamen = Object.values(CLIENTEN).flat().map(c => `${c.voornaam} ${c.achternaam}`);

  // Bereken per contactmoment de juiste doeltitel in de doelenlijst van de gekozen cliënt
  const berekenDoelWijzigingen = (momenten, clientNaam) => {
    const lijst = getClientDoelenByName(clientNaam) || [];
    return (momenten || [])
      .filter(m => m.doel_id !== null && m.doel_id !== undefined)
      .map(m => {
        const doel = lijst.find(d => d.id === m.doel_id);
        return { moment_index: m.moment_index, moment_naam: m.moment_naam, doel_id: m.doel_id, van: m.doel_titel, naar: doel ? doel.titel : null };
      });
  };

  const corrigeerRapportage = async (rap, momenten) => {
    const nieuweClient = correctie.client || rap.client_naam;
    const wijzigtClient = nieuweClient.toLowerCase() !== (rap.client_naam || '').toLowerCase();
    const wijzigingen = correctie.doelenWissen ? [] : berekenDoelWijzigingen(momenten, nieuweClient);
    const doelenAnders = correctie.doelenWissen || wijzigingen.some(w => w.van !== w.naar);
    if (!wijzigtClient && !doelenAnders) {
      setCorrectie(c => ({ ...c, melding: 'Deze rapportage klopt al: zelfde cliënt en dezelfde doeltitels.' }));
      return;
    }
    const regels = [];
    if (wijzigtClient) regels.push(`Cliënt: ${rap.client_naam} → ${nieuweClient}`);
    if (correctie.doelenWissen) regels.push('Alle gekoppelde doelen worden gewist.');
    wijzigingen.filter(w => w.van !== w.naar).forEach(w => regels.push(`${w.moment_naam}, doel ${w.doel_id}: "${w.van || '-'}" → "${w.naar || '(gewist, bestaat niet bij deze cliënt)'}"`));
    if (!window.confirm(`Deze correctie opslaan?\n\n${regels.join('\n')}`)) return;
    setCorrectie(c => ({ ...c, bezig: true, melding: '' }));
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const doel_titels = {};
      wijzigingen.forEach(w => { doel_titels[String(w.moment_index)] = w.naar; });
      const response = await fetch(`${backendUrl}/api/sjabloon-rapportages/${rap.id}/correctie`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_naam: nieuweClient, doelen_wissen: correctie.doelenWissen, doel_titels })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Corrigeren mislukt');
      const r = data.rapportage;
      setSelectedRapportage(prev => ({ ...prev, client_naam: r.client_naam, sjabloonData: r }));
      setCorrectie({ client: '', doelenWissen: false, bezig: false, melding: `✓ Opgeslagen: rapportage staat bij ${r.client_naam}` });
      fetchSjabloonRapportages();
    } catch (err) {
      setCorrectie(c => ({ ...c, bezig: false, melding: `Fout: ${err.message}` }));
    }
  };

  // Fetch OUDE rapportage-sjablonen (3 contactmomenten)
  const fetchSjablonen = React.useCallback(async () => {
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    try {
      const params = new URLSearchParams();
      if (filters.begeleider) params.append('begeleider', filters.begeleider);
      if (filters.client) params.append('client', filters.client);
      if (filters.datumVan) params.append('datum_van', filters.datumVan);
      if (filters.datumTot) params.append('datum_tot', filters.datumTot);

      const response = await fetch(`${backendUrl}/api/rapportage-sjablonen?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSjablonen(data.sjablonen || []);
    } catch (err) {
      console.error('Kon oude sjablonen niet laden:', err);
    }
  }, [filters]);

  // Fetch NIEUWE sjabloon-flow rapportages (2 contactmomenten + doel data)
  const fetchSjabloonRapportages = React.useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    try {
      const response = await fetch(`${backendUrl}/api/sjabloon-rapportages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Filter op basis van filters
      let rapportages = data.rapportages || [];
      if (filters.begeleider) {
        rapportages = rapportages.filter(r => 
          r.begeleider_naam?.toLowerCase().includes(filters.begeleider.toLowerCase())
        );
      }
      if (filters.client) {
        rapportages = rapportages.filter(r => 
          r.client_naam?.toLowerCase().includes(filters.client.toLowerCase())
        );
      }
      if (filters.datumVan) {
        rapportages = rapportages.filter(r => r.datum >= filters.datumVan);
      }
      if (filters.datumTot) {
        rapportages = rapportages.filter(r => r.datum <= filters.datumTot);
      }
      
      setSjabloonRapportages(rapportages);
    } catch (err) {
      console.error('Kon sjabloon rapportages niet laden:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Combineer beide bronnen tot één lijst
  const combineRapportages = React.useCallback(() => {
    const combined = [];
    
    // Voeg NIEUWE sjabloon-rapportages toe (markeer als nieuw)
    sjabloonRapportages.forEach(rap => {
      combined.push({
        id: rap.id,
        client_naam: rap.client_naam,
        begeleider_naam: rap.begeleider_naam,
        datum: rap.datum,
        created_at: rap.created_at,
        isNieuweFlow: true,
        sjabloonData: rap,
        doelData: null
      });
    });
    
    // Voeg OUDE sjablonen toe (markeer als oud) - maar NIET als dezelfde sessie al als nieuwe
    // rapportage bestaat (elke sessie wordt in beide opslagen bewaard; ticket #268311).
    oudeZonderNieuweTweeling(sjabloonRapportages, sjablonen).forEach(sjabloon => {
      combined.push({
        id: sjabloon.id,
        client_naam: sjabloon.client_naam,
        begeleider_naam: sjabloon.begeleider_naam,
        datum: sjabloon.datum,
        created_at: sjabloon.created_at,
        isNieuweFlow: false,
        sjabloonData: null,
        doelData: sjabloon
      });
    });
    
    // Sorteer op datum (nieuwste eerst)
    combined.sort((a, b) => {
      const dateCompare = new Date(b.datum) - new Date(a.datum);
      if (dateCompare !== 0) return dateCompare;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
    
    setAlleRapportages(combined);
  }, [sjabloonRapportages, sjablonen]);

  // Stats ophalen (alleen voor admin)
  const fetchStats = React.useCallback(async () => {
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${backendUrl}/api/rapportage-sjablonen/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Kon stats niet laden:', err);
    }
  }, []);

  useEffect(() => {
    fetchSjablonen();
    fetchSjabloonRapportages();
    if (isAdmin) {
      fetchStats();
    }
  }, [fetchSjablonen, fetchSjabloonRapportages, fetchStats, isAdmin]);

  // Combineer rapportages wanneer data verandert
  useEffect(() => {
    if (!loading) {
      combineRapportages();
    }
  }, [sjabloonRapportages, sjablonen, loading, combineRapportages]);

  // Merge duplicates functie voor admin
  const mergeDuplicates = async () => {
    if (!window.confirm('Weet je zeker dat je duplicate rapportages wilt samenvoegen? Dit kan niet ongedaan worden gemaakt.')) {
      return;
    }
    
    setMerging(true);
    setMergeResult(null);
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${backendUrl}/api/rapportage-sjablonen/merge-duplicates`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      setMergeResult(data);
      
      // Herlaad de lijst
      fetchSjablonen();
    } catch (err) {
      setMergeResult({ error: 'Kon duplicates niet samenvoegen' });
    } finally {
      setMerging(false);
    }
  };

  const PGB_VRAGEN_LABELS = {
    'aanleiding': 'Aanleiding',
    'doel': 'Doel',
    'activiteit': 'Wat is gedaan',
    'waarom': 'Waarom begeleiding',
    'reactie': 'Reactie cliënt',
    'resultaat': 'Resultaat',
    'doelen': 'Zorgdoelen'
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-400';
    if (score >= 70) return 'text-amber-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 85) return 'bg-emerald-500/20 border-emerald-500/50';
    if (score >= 70) return 'bg-amber-500/20 border-amber-500/50';
    return 'bg-red-500/20 border-red-500/50';
  };

  // GECOMBINEERDE DETAIL VIEW
  if (selectedRapportage) {
    const { client_naam, begeleider_naam, datum, sjabloonData, doelData } = selectedRapportage;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedRapportage(null)}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-slate-300" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">{client_naam}</h1>
                <p className="text-sm text-slate-400">
                  Door: {begeleider_naam} • {datum}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {/* Info card */}
          <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-slate-400">Cliënt</div>
                <div className="text-white font-medium">{client_naam}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Begeleider</div>
                <div className="text-white font-medium">{begeleider_naam}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Datum</div>
                <div className="text-white font-medium">{datum}</div>
              </div>
            </div>
            {/* Status indicators */}
            <div className="mt-4 flex flex-wrap gap-2">
              {sjabloonData && (
                <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-sm">
                  📋 {sjabloonData.contactmomenten?.length || 0} Contactmomenten ingevuld
                </span>
              )}
              {sjabloonData?.contactmomenten?.some(m => m.doel_titel) && (
                <span className="px-3 py-1 rounded-full bg-violet-500/20 text-violet-400 text-sm">
                  🎯 Doelen gekoppeld
                </span>
              )}
            </div>
          </div>

          {/* CORRECTIE: verkeerde cliënt of doelen (alleen beheerders) */}
          {isAdmin && sjabloonData?.id && (() => {
            const doelClient = correctie.client || client_naam;
            const voorbeeld = correctie.doelenWissen ? [] : berekenDoelWijzigingen(sjabloonData.contactmomenten, doelClient);
            return (
            <div className="bg-amber-500/10 rounded-2xl p-5 border border-amber-500/40" data-testid="correctie-panel">
              <div className="text-amber-300 font-semibold mb-1">Cliënt of doelen corrigeren</div>
              <p className="text-sm text-slate-400 mb-3">
                Staat deze rapportage bij de verkeerde cliënt, of kloppen de doeltitels niet? Kies de juiste cliënt; de doeltitels worden dan uit de doelenlijst van die cliënt gehaald.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <select
                  value={doelClient}
                  onChange={(e) => setCorrectie(c => ({ ...c, client: e.target.value, melding: '' }))}
                  className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  data-testid="correctie-client-select"
                >
                  {!alleClientNamen.includes(client_naam) && <option value={client_naam}>{client_naam}</option>}
                  {alleClientNamen.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={correctie.doelenWissen}
                    onChange={(e) => setCorrectie(c => ({ ...c, doelenWissen: e.target.checked, melding: '' }))}
                    data-testid="correctie-doelen-wissen"
                  />
                  Doelen wissen
                </label>
                <button
                  onClick={() => corrigeerRapportage({ id: sjabloonData.id, client_naam }, sjabloonData.contactmomenten)}
                  disabled={correctie.bezig}
                  className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold disabled:opacity-50"
                  data-testid="correctie-opslaan"
                >
                  {correctie.bezig ? 'Bezig…' : 'Correctie opslaan'}
                </button>
              </div>
              {voorbeeld.length > 0 && (
                <div className="mt-3 text-sm text-slate-300 space-y-1" data-testid="correctie-doelen-voorbeeld">
                  {voorbeeld.map(w => (
                    <div key={w.moment_index}>
                      {w.moment_naam}, doel {w.doel_id}: <span className="text-slate-400">{w.van || '-'}</span>
                      {w.van !== w.naar
                        ? <> → <span className="text-amber-200">{w.naar || '(wordt gewist: dit doelnummer bestaat niet bij deze cliënt)'}</span></>
                        : <span className="text-emerald-400"> ✓</span>}
                    </div>
                  ))}
                </div>
              )}
              {correctie.melding && <div className="mt-3 text-sm text-amber-200">{correctie.melding}</div>}
            </div>
            );
          })()}

          {/* STAP 1 & 2: Contactmomenten (uit sjabloonData) */}
          {sjabloonData?.contactmomenten?.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">1</span>
                Contactmomenten ({sjabloonData.contactmomenten.length})
              </h2>
              
              {sjabloonData.contactmomenten.map((moment, idx) => (
                <div key={`contactmoment-${idx}`} className="bg-slate-800/50 rounded-2xl border border-cyan-500/30 overflow-hidden">
                  <div className="p-4 bg-cyan-900/20 border-b border-cyan-500/30">
                    <h3 className="font-semibold text-cyan-400 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-cyan-500/30 flex items-center justify-center text-sm">
                        {idx + 1}
                      </span>
                      {moment.moment_naam || `Contactmoment ${idx + 1}`}
                    </h3>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {/* Sjabloon Vragen & Antwoorden */}
                    {moment.antwoorden && Object.values(moment.antwoorden).some(v => v?.trim()) && (
                      <div>
                        <div className="text-xs text-amber-400 uppercase tracking-wide mb-3 font-semibold flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Sjabloon Vragen & Antwoorden
                        </div>
                        <div className="grid gap-2">
                          {[
                            { key: 'aanleiding', label: '1. Aanleiding' },
                            { key: 'doel', label: '2. Doel' },
                            { key: 'activiteit', label: '3. Activiteiten' },
                            { key: 'waarom', label: '4. Waarom begeleiding nodig' },
                            { key: 'reactie', label: '5. Reactie cliënt' },
                            { key: 'resultaat', label: '6. Resultaat' },
                            { key: 'bijzonderheden', label: '📌 Bijzonderheden' }
                          ].map(({ key, label }) => (
                            moment.antwoorden[key]?.trim() && (
                              <div key={key} className="p-3 rounded-lg bg-slate-700/50 border border-slate-600/50">
                                <span className="text-amber-400 font-medium text-sm">{label}:</span>
                                <p className="text-slate-200 mt-1">{moment.antwoorden[key]}</p>
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Gegenereerde Rapportage voor contactmoment */}
                    {moment.standaard_rapportage && (
                      <div>
                        <div className="text-xs text-emerald-400 uppercase tracking-wide mb-2 font-semibold flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Gegenereerde Rapportage
                        </div>
                        <div className="bg-emerald-900/20 rounded-lg p-4 text-slate-200 whitespace-pre-wrap border border-emerald-500/30 text-sm">
                          {moment.standaard_rapportage}
                        </div>
                      </div>
                    )}
                    
                    {/* Doel info binnen contactmoment (Stap 2 van sjabloon flow) */}
                    {(moment.doel_titel || moment.doel_rapportage) && (
                      <div className="mt-4 pt-4 border-t border-slate-600/50">
                        {moment.doel_titel && (
                          <>
                            <div className="text-xs text-violet-400 uppercase tracking-wide mb-2 font-semibold flex items-center gap-2">
                              <Target className="w-4 h-4" />
                              Gekoppeld Doel
                            </div>
                            <div className="p-3 rounded-lg bg-violet-900/20 border border-violet-500/30">
                              <div className="font-semibold text-violet-300 mb-2">{moment.doel_titel}</div>
                              {moment.doel_vragen && (
                                <div className="grid gap-2 mt-2">
                                  {moment.doel_vragen.begeleiding && (
                                    <div className="p-2 rounded bg-slate-800/50">
                                      <span className="text-violet-400 font-medium text-sm">Begeleiding:</span>
                                      <p className="text-slate-200 text-sm">{moment.doel_vragen.begeleiding}</p>
                                    </div>
                                  )}
                                  {moment.doel_vragen.reactie && (
                                    <div className="p-2 rounded bg-slate-800/50">
                                      <span className="text-violet-400 font-medium text-sm">Reactie:</span>
                                      <p className="text-slate-200 text-sm">{moment.doel_vragen.reactie}</p>
                                    </div>
                                  )}
                                  {moment.doel_vragen.resultaat && (
                                    <div className="p-2 rounded bg-slate-800/50">
                                      <span className="text-violet-400 font-medium text-sm">Resultaat:</span>
                                      <p className="text-slate-200 text-sm">{moment.doel_vragen.resultaat}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {/* Gegenereerde Doel Rapportage - onafhankelijk van doel_titel, want cliënten met
                            een vrij tekstveld voor hun doel hebben geen doel_titel */}
                        {moment.doel_rapportage && (
                          <div className="mt-3">
                            <div className="text-xs text-violet-400 uppercase tracking-wide mb-2 font-semibold">
                              Gegenereerde Doel Rapportage
                            </div>
                            <div className="bg-violet-900/20 rounded-lg p-4 text-slate-200 whitespace-pre-wrap border border-violet-500/30 text-sm">
                              {moment.doel_rapportage}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Als er geen data is */}
                    {!moment.antwoorden?.aanleiding && !moment.standaard_rapportage && !moment.doel_titel && !moment.doel_rapportage && (
                      <p className="text-slate-500 italic text-sm">Dit contactmoment is nog niet ingevuld</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Doel-rapportages (uit doelData - oude flow) */}
          {doelData?.doel_rapportages?.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400">2</span>
                Zorgdoelen ({doelData.doel_rapportages.length})
              </h2>
              
              {doelData.doel_rapportages.map((doel, doelIdx) => {
                const SMILEY_MAP = {
                  'goed': { emoji: '😊', label: 'Goed gelukt', color: 'emerald' },
                  'redelijk': { emoji: '😐', label: 'Ging redelijk', color: 'amber' },
                  'moeilijk': { emoji: '😟', label: 'Was moeilijk', color: 'red' }
                };
                const smileyInfo = SMILEY_MAP[doel.smiley] || { emoji: '❓', label: doel.smiley, color: 'slate' };
                
                // Haal de volledige doel definitie op met alle vragen
                const doelDefinitie = findDoelById(doelData.client_naam, doel.doel_id);
                const alleVragen = doelDefinitie?.vragen || [];
                const antwoorden = doel.antwoorden || {};
                
                // Tel beantwoorde vragen
                const beantwoordeVragen = alleVragen.filter(v => antwoorden[v.id]?.trim()).length;
                const totaalVragen = alleVragen.length;
                
                return (
                  <div key={`doel-${doel.doel_id}-${doelIdx}`} className="bg-slate-800/50 rounded-2xl border border-violet-500/30 overflow-hidden">
                    <div className={`p-4 border-b border-slate-600/50 bg-violet-900/20`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                              Doel {doel.doel_id}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300">
                              {doel.categorie}
                            </span>
                            {totaalVragen > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">
                                {beantwoordeVragen}/{totaalVragen} vragen
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-white">{doel.doel_titel}</h3>
                          <p className="text-sm text-slate-400 mt-1">{doel.doel_beschrijving}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-3xl">{smileyInfo.emoji}</span>
                          <p className={`text-xs text-${smileyInfo.color}-400`}>{smileyInfo.label}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      {/* Gegenereerde Rapportage voor dit doel */}
                      {doel.gegenereerde_rapportage && (
                        <div className="mb-4 p-4 rounded-xl bg-emerald-900/20 border border-emerald-500/30">
                          <h4 className="text-sm font-semibold text-emerald-300 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Gegenereerde Rapportage
                          </h4>
                          <div className="text-sm text-slate-200 whitespace-pre-wrap">
                            {doel.gegenereerde_rapportage}
                          </div>
                        </div>
                      )}
                      
                      {/* Antwoorden weergeven */}
                      {Object.keys(antwoorden).length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Ingevulde antwoorden</p>
                          {Object.entries(antwoorden).map(([vraagId, antwoord]) => (
                            antwoord?.trim() && (
                              <div key={vraagId} className="p-3 rounded-lg bg-slate-700/50 border border-slate-600/50">
                                <span className="text-violet-400 font-medium text-sm">
                                  {vraagId.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}:
                                </span>
                                <p className="text-slate-200 text-sm mt-1">{antwoord}</p>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Als er helemaal geen data is */}
          {!sjabloonData && !doelData && (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <p className="text-slate-400">Geen rapportage data gevonden voor deze combinatie</p>
            </div>
          )}

          {/* OUDE STIJL RAPPORTAGES (3 contactmomenten) */}
          {!sjabloonData && doelData && doelData.contact_momenten?.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm">
                  📋 Klassiek sjabloon
                </span>
              </div>
              
              <h2 className="text-lg font-semibold text-white">Contactmomenten ({doelData.contact_momenten.length})</h2>
              
              {doelData.contact_momenten.map((moment, idx) => {
                const heeftAntwoorden = Object.keys(moment.antwoorden || {}).length > 0;
                const volledigIngevuld = Object.values(moment.antwoorden || {}).filter(v => v && v.trim()).length >= 7;
                
                return (
                  <div key={`moment-${idx}`} className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
                    <div className={`p-4 border-b border-slate-600/50 flex items-center justify-between ${
                      volledigIngevuld ? 'bg-emerald-700/20' : heeftAntwoorden ? 'bg-amber-700/20' : 'bg-slate-700/30'
                    }`}>
                      <h3 className="font-semibold text-white">{moment.naam}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${
                        volledigIngevuld ? 'bg-emerald-500/20 text-emerald-400' : 
                        heeftAntwoorden ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {volledigIngevuld ? '✓ Volledig' : heeftAntwoorden ? 'Deels ingevuld' : 'Nog niet ingevuld'}
                      </span>
                    </div>
                    <div className="p-4 space-y-4">
                      {heeftAntwoorden ? (
                        Object.entries(moment.antwoorden || {}).map(([key, value]) => (
                          <div key={key} className={`p-3 rounded-lg ${value?.trim() ? 'bg-emerald-900/20 border border-emerald-500/30' : 'bg-red-900/20 border border-red-500/30'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              {value?.trim() ? (
                                <CheckCircle className="w-4 h-4 text-emerald-400" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-red-400" />
                              )}
                              <span className="text-sm font-medium text-slate-300">
                                {PGB_VRAGEN_LABELS[key] || key}
                              </span>
                            </div>
                            <p className={`text-sm ${value?.trim() ? 'text-slate-300' : 'text-red-400 italic'}`}>
                              {value?.trim() || 'Niet ingevuld'}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-slate-500 italic">
                          Dit contactmoment is nog niet ingevuld door de begeleider
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Gegenereerde rapportage voor oude stijl */}
              {doelData.gegenereerde_rapportage && (
                <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
                  <h2 className="text-lg font-semibold text-white mb-4">Gegenereerde Rapportage</h2>
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-600/50">
                    <pre className="whitespace-pre-wrap text-slate-200 text-sm font-sans">
                      {doelData.gegenereerde_rapportage}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-300" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">Rapportage Controle</h1>
                <p className="text-sm text-slate-400">
                  {isAdmin ? 'Controleer alle ingevulde sjablonen' : 'Bekijk je eigen rapportages'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors"
            >
              <Filter className="w-4 h-4 text-slate-300" />
              <span className="text-slate-300">Filters</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats cards (alleen voor admin) */}
        {isAdmin && stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-violet-500/20">
                  <FileText className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.totaal_rapportages}</div>
                  <div className="text-sm text-slate-400">Totaal rapportages</div>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-red-500/20">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-400">{stats.lage_scores_count}</div>
                  <div className="text-sm text-slate-400">Lage scores (&lt;70%)</div>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-emerald-500/20">
                  <BarChart3 className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.per_begeleider?.length || 0}</div>
                  <div className="text-sm text-slate-400">Actieve begeleiders</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin tools - Merge duplicates */}
        {isAdmin && (
          <div className="bg-slate-800/50 rounded-2xl p-4 border border-slate-700/50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium">Duplicates samenvoegen</h3>
                <p className="text-sm text-slate-400">Voeg rapportages samen die dezelfde begeleider, cliënt, datum en dienst hebben</p>
              </div>
              <button
                onClick={mergeDuplicates}
                disabled={merging}
                className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                  merging 
                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed' 
                    : 'bg-violet-500 text-white hover:bg-violet-600'
                }`}
              >
                {merging ? 'Bezig...' : 'Merge Duplicates'}
              </button>
            </div>
            {mergeResult && (
              <div className={`mt-3 p-3 rounded-xl ${mergeResult.error ? 'bg-red-500/10 border border-red-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
                {mergeResult.error ? (
                  <p className="text-red-400 text-sm">{mergeResult.error}</p>
                ) : (
                  <p className="text-emerald-400 text-sm">
                    ✓ {mergeResult.groups_merged} groepen samengevoegd, {mergeResult.duplicates_deleted} duplicates verwijderd
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {isAdmin && (
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Begeleider</label>
                  <input
                    type="text"
                    value={filters.begeleider}
                    onChange={(e) => setFilters({ ...filters, begeleider: e.target.value })}
                    placeholder="Zoek begeleider..."
                    className="w-full p-2 rounded-lg bg-slate-900 border border-slate-600 text-white placeholder-slate-500"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Cliënt</label>
                <input
                  type="text"
                  value={filters.client}
                  onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                  placeholder="Zoek cliënt..."
                  className="w-full p-2 rounded-lg bg-slate-900 border border-slate-600 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Datum van</label>
                <input
                  type="date"
                  value={filters.datumVan}
                  onChange={(e) => setFilters({ ...filters, datumVan: e.target.value })}
                  className="w-full p-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Datum tot</label>
                <input
                  type="date"
                  value={filters.datumTot}
                  onChange={(e) => setFilters({ ...filters, datumTot: e.target.value })}
                  className="w-full p-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                />
              </div>
            </div>
            <button
              onClick={() => { fetchSjablonen(); fetchSjabloonRapportages(); }}
              className="mt-4 px-6 py-2 rounded-xl bg-violet-500 text-white font-medium hover:bg-violet-600 transition-colors"
            >
              Zoeken
            </button>
          </div>
        )}

        {/* Rapportages Overzicht Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-cyan-500/20 to-violet-500/20">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Alle Rapportages</h2>
              <p className="text-sm text-slate-400">
                {alleRapportages.length} rapportage{alleRapportages.length !== 1 ? 's' : ''} gevonden
                {sjabloonRapportages.length > 0 && sjablonen.length > 0 && (
                  <span className="ml-2 text-xs">
                    ({sjabloonRapportages.length} nieuw, {sjablonen.length} klassiek)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-red-300">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-400">Laden...</p>
          </div>
        ) : alleRapportages.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Geen rapportages gevonden</p>
            <p className="text-slate-500 text-sm mt-2">Pas je filters aan of wacht tot er rapportages zijn ingediend</p>
          </div>
        ) : (
          /* GECOMBINEERDE LIJST van alle rapportages */
          <div className="space-y-3">
            {alleRapportages.map((rap, idx) => {
              // Bepaal of dit een nieuwe of oude flow rapportage is
              const isNieuw = rap.isNieuweFlow;
              
              // Bereken voortgang
              let contactProgress = null;
              if (isNieuw && rap.sjabloonData?.contactmomenten) {
                const volledige = rap.sjabloonData.contactmomenten.filter(m => 
                  m.standaard_rapportage || m.doel_rapportage
                ).length;
                const totaal = rap.sjabloonData.contactmomenten.length;
                contactProgress = { volledige, totaal };
              } else if (!isNieuw && rap.doelData?.contact_momenten) {
                const volledige = rap.doelData.contact_momenten.filter(m => 
                  Object.values(m.antwoorden || {}).some(v => v && v.trim())
                ).length;
                const totaal = rap.doelData.contact_momenten.length;
                contactProgress = { volledige, totaal };
              }
              
              // Haal created_at op voor meer context
              const createdAt = rap.created_at;
              const timeString = createdAt ? new Date(createdAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : null;
              
              // Preview van de gegenereerde rapportage voor herkenning
              const rapportagePreview = isNieuw 
                ? rap.sjabloonData?.contactmomenten?.[0]?.standaard_rapportage?.substring(0, 80)
                : rap.doelData?.gegenereerde_rapportage?.substring(0, 80);
              
              return (
                <div
                  key={`rap-${rap.id || idx}-${isNieuw ? 'new' : 'old'}`}
                  onClick={() => setSelectedRapportage(rap)}
                  className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 hover:border-violet-500/50 cursor-pointer transition-all group"
                  data-testid={`rapportage-item-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isNieuw 
                          ? 'bg-gradient-to-br from-cyan-500/20 to-violet-500/20' 
                          : 'bg-gradient-to-br from-amber-500/20 to-orange-500/20'
                      }`}>
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-white group-hover:text-violet-300 transition-colors">
                          {rap.client_naam}
                        </div>
                        <div className="text-sm text-slate-400">
                          Door: {rap.begeleider_naam} • {rap.datum}{timeString ? ` om ${timeString}` : ''}
                        </div>
                        {/* Preview van rapportage tekst voor herkenning */}
                        {rapportagePreview && (
                          <div className="text-xs text-slate-500 mt-1 italic line-clamp-1">
                            "{rapportagePreview}..."
                          </div>
                        )}
                        {/* Badges voor wat er aanwezig is */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {isNieuw ? (
                            <>
                              <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs">
                                📋 {contactProgress?.volledige || 0}/{contactProgress?.totaal || 0} contactmomenten
                              </span>
                              {rap.sjabloonData?.contactmomenten?.some(m => m.doel_titel) && (
                                <span className="px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-xs">
                                  🎯 Doelen gekoppeld
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs">
                                📋 {contactProgress?.volledige || 0}/{contactProgress?.totaal || 0} momenten
                              </span>
                              <span className="px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400 text-xs">
                                Klassiek sjabloon
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Eye className="w-5 h-5 text-slate-400 group-hover:text-violet-400 transition-colors" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Per begeleider stats (alleen voor admin) */}
        {isAdmin && stats?.per_begeleider?.length > 0 && (
          <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
            <h2 className="text-lg font-semibold text-white mb-4">Statistieken per Begeleider</h2>
            <div className="space-y-3">
              {stats.per_begeleider.map((item) => (
                <div key={`stats-${item._id || 'unknown'}`} className="flex items-center justify-between p-3 rounded-xl bg-slate-900/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-violet-400" />
                    </div>
                    <span className="text-white font-medium">{item._id || 'Onbekend'}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">{item.totaal}</div>
                      <div className="text-xs text-slate-400">rapportages</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${getScoreColor(item.gemiddelde_score)}`}>
                        {Math.round(item.gemiddelde_score || 0)}%
                      </div>
                      <div className="text-xs text-slate-400">gem. score</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RapportageControlePage;
